import React, { useState } from 'react';
import { TrendingUp, Users, MessageCircle, Star, Calendar, BookOpen, ShoppingBag, Award, Eye, Heart, Clock, Plus, Edit2, BarChart3 } from 'lucide-react';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { showToast } from '../utils/toast';

interface ExpertDashboardProps {
  onNavigate: (section: string, data?: any) => void;
}

const ExpertDashboard: React.FC<ExpertDashboardProps> = ({ onNavigate }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showCreateContent, setShowCreateContent] = useState(false);
  
  // Mock expert data - in real app this would come from backend
  const [expertStats] = useLocalStorage('expert-stats', {
    followers: 1250,
    totalAnswers: 340,
    helpfulVotes: 890,
    articlesPublished: 23,
    sessionsHosted: 15,
    totalViews: 45600,
    monthlyGrowth: 12.5,
    engagementRate: 8.7
  });

  const [recentActivity] = useLocalStorage('expert-activity', [
    {
      id: '1',
      type: 'answer',
      content: 'Answered question about hypertension management',
      timestamp: '2 hours ago',
      engagement: 15
    },
    {
      id: '2',
      type: 'article',
      content: 'Published "Managing Diabetes in Ghana"',
      timestamp: '1 day ago',
      engagement: 89
    },
    {
      id: '3',
      type: 'session',
      content: 'Hosted live session on mental health',
      timestamp: '3 days ago',
      engagement: 156
    }
  ]);

  const [upcomingSessions] = useLocalStorage('expert-sessions', [
    {
      id: '1',
      title: 'Nutrition During Pregnancy',
      date: '2024-01-25',
      time: '15:00',
      registered: 45,
      maxAttendees: 100
    },
    {
      id: '2',
      title: 'Managing Chronic Pain',
      date: '2024-01-28',
      time: '18:00',
      registered: 23,
      maxAttendees: 50
    }
  ]);

  const StatCard = ({ title, value, icon: Icon, trend, color, subtitle }: any) => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="text-white" size={24} />
        </div>
        {trend && (
          <div className="flex items-center text-green-600">
            <TrendingUp size={16} />
            <span className="ml-1 text-sm font-medium">+{trend}%</span>
          </div>
        )}
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-gray-600 text-sm">{title}</p>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </div>
    </div>
  );

  const CreateContentModal = () => {
    const [contentType, setContentType] = useState('article');
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (title.trim() && content.trim()) {
        showToast.expert.contentCreated(contentType);
        setShowCreateContent(false);
        setTitle('');
        setContent('');
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-xl font-semibold text-gray-900">Create New Content</h3>
            <button
              onClick={() => setShowCreateContent(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              ×
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Content Type</label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { id: 'article', label: 'Health Article', icon: BookOpen },
                  { id: 'session', label: 'Live Session', icon: Calendar },
                  { id: 'qa', label: 'Q&A Post', icon: MessageCircle }
                ].map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setContentType(type.id)}
                      className={`p-4 border-2 rounded-xl transition-all duration-200 hover:scale-105 ${
                        contentType === type.id
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Icon size={24} className="mx-auto mb-2" />
                      <p className="text-sm font-medium">{type.label}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
              <input
                type="text"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder={
                  contentType === 'article' ? 'e.g., Managing Hypertension in Ghana' :
                  contentType === 'session' ? 'e.g., Live Q&A: Mental Health Awareness' :
                  'e.g., Common Questions About Diabetes'
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {contentType === 'article' ? 'Article Content' : 
                 contentType === 'session' ? 'Session Description' : 
                 'Q&A Content'}
              </label>
              <textarea
                required
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={8}
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                placeholder={
                  contentType === 'article' ? 'Write your health article...' :
                  contentType === 'session' ? 'Describe what you\'ll cover in this session...' :
                  'Share your expert knowledge...'
                }
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowCreateContent(false)}
                className="px-6 py-3 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors"
              >
                Publish {contentType === 'article' ? 'Article' : contentType === 'session' ? 'Session' : 'Post'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 pb-20 lg:pb-0">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Expert Dashboard</h1>
              <p className="text-gray-600">Manage your professional presence on PharmaPlus</p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 bg-green-100 text-green-800 px-3 py-2 rounded-lg">
                <Award size={16} />
                <span className="text-sm font-medium">Verified Expert</span>
              </div>
              <button
                onClick={() => setShowCreateContent(true)}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-xl hover:bg-green-700 transition-colors"
              >
                <Plus size={16} />
                <span>Create Content</span>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 bg-white/60 backdrop-blur-sm rounded-2xl p-2 border border-gray-200">
            {[
              { id: 'overview', label: 'Overview', icon: BarChart3 },
              { id: 'content', label: 'Content', icon: BookOpen },
              { id: 'sessions', label: 'Sessions', icon: Calendar },
              { id: 'analytics', label: 'Analytics', icon: TrendingUp }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-3 rounded-xl font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-white text-green-700 shadow-md'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <Icon size={18} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Followers"
                value={expertStats.followers.toLocaleString()}
                icon={Users}
                trend={expertStats.monthlyGrowth}
                color="bg-blue-500"
                subtitle="This month"
              />
              <StatCard
                title="Total Answers"
                value={expertStats.totalAnswers}
                icon={MessageCircle}
                color="bg-green-500"
                subtitle="Community Q&A"
              />
              <StatCard
                title="Helpful Votes"
                value={expertStats.helpfulVotes}
                icon={Heart}
                color="bg-red-500"
                subtitle="From community"
              />
              <StatCard
                title="Total Views"
                value={`${(expertStats.totalViews / 1000).toFixed(1)}K`}
                icon={Eye}
                color="bg-purple-500"
                subtitle="All content"
              />
            </div>

            {/* Recent Activity & Upcoming Sessions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
                <div className="space-y-4">
                  {recentActivity.map((activity) => (
                    <div key={activity.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className={`p-2 rounded-lg ${
                        activity.type === 'answer' ? 'bg-green-100' :
                        activity.type === 'article' ? 'bg-blue-100' :
                        'bg-purple-100'
                      }`}>
                        {activity.type === 'answer' && <MessageCircle size={16} className="text-green-600" />}
                        {activity.type === 'article' && <BookOpen size={16} className="text-blue-600" />}
                        {activity.type === 'session' && <Calendar size={16} className="text-purple-600" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-800 text-sm">{activity.content}</p>
                        <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500">
                          <span>{activity.timestamp}</span>
                          <span>•</span>
                          <span>{activity.engagement} engagements</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Sessions</h3>
                <div className="space-y-4">
                  {upcomingSessions.map((session) => (
                    <div key={session.id} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                      <h4 className="font-medium text-gray-900 mb-2">{session.title}</h4>
                      <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                        <span className="flex items-center space-x-1">
                          <Calendar size={14} />
                          <span>{session.date}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Clock size={14} />
                          <span>{session.time}</span>
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">
                          {session.registered}/{session.maxAttendees} registered
                        </span>
                        <button className="text-green-600 hover:text-green-700 text-sm font-medium">
                          Manage
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'content' && (
          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 text-center">
            <BookOpen className="mx-auto text-gray-400 mb-4" size={64} />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Content Management</h3>
            <p className="text-gray-600 mb-6">Manage your articles, Q&A responses, and educational content</p>
            <button
              onClick={() => setShowCreateContent(true)}
              className="bg-green-600 text-white px-6 py-3 rounded-xl hover:bg-green-700 transition-colors"
            >
              Create New Content
            </button>
          </div>
        )}

        {activeTab === 'sessions' && (
          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 text-center">
            <Calendar className="mx-auto text-gray-400 mb-4" size={64} />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Session Management</h3>
            <p className="text-gray-600 mb-6">Schedule and manage your live expert sessions</p>
            <button className="bg-green-600 text-white px-6 py-3 rounded-xl hover:bg-green-700 transition-colors">
              Schedule New Session
            </button>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 text-center">
            <TrendingUp className="mx-auto text-gray-400 mb-4" size={64} />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Analytics & Insights</h3>
            <p className="text-gray-600 mb-6">Track your impact and engagement metrics</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{expertStats.engagementRate}%</p>
                <p className="text-blue-800 text-sm">Engagement Rate</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{expertStats.articlesPublished}</p>
                <p className="text-green-800 text-sm">Articles Published</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <p className="text-2xl font-bold text-purple-600">{expertStats.sessionsHosted}</p>
                <p className="text-purple-800 text-sm">Sessions Hosted</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create Content Modal */}
      {showCreateContent && <CreateContentModal />}
    </div>
  );
};

export default ExpertDashboard;